
import { SupabaseClient } from "@supabase/supabase-js";
import { RawEmailData } from "../types";

export interface EmailFilters {
  search?: string;
  category?: string;
  tag?: string;
  processingStatus?: string;
  isArchived?: boolean;
  isFlagged?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
  direction?: 'all' | 'incoming' | 'outgoing'; // Added direction filter
  assignedTo?: string;
  priority?: 'low' | 'medium' | 'high';
  sentiment?: string;
  limit?: number;
  offset?: number;
}

export const buildEmailQuery = (supabase: SupabaseClient, filters: EmailFilters) => {
  let query = supabase
    .from('processed_emails')
    .select('*')
    .order('received_at', { ascending: false });

  // Apply direction filter
  if (filters.direction && filters.direction !== 'all') {
    query = query.eq('email_direction', filters.direction);
  }

  // Apply other existing filters
  if (filters.search) {
    query = query.or(`subject.ilike.%${filters.search}%,sender.ilike.%${filters.search}%,summary.ilike.%${filters.search}%`);
  }

  if (filters.processingStatus) {
    query = query.eq('processing_status', filters.processingStatus);
  }

  if (filters.isArchived !== undefined) {
    query = query.eq('is_archived', filters.isArchived);
  }

  if (filters.isFlagged !== undefined) {
    query = query.eq('is_flagged', filters.isFlagged);
  }

  if (filters.dateFrom) {
    query = query.gte('received_at', filters.dateFrom.toISOString());
  }

  if (filters.dateTo) {
    query = query.lte('received_at', filters.dateTo.toISOString());
  }

  if (filters.sentiment) {
    query = query.eq('sentiment', filters.sentiment);
  }

  if (filters.limit) {
    query = query.limit(filters.limit);
  }

  if (filters.offset) {
    query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
  }

  return query;
};

export const applyClientSideFilters = (emails: RawEmailData[], filters: EmailFilters): RawEmailData[] => {
  let filteredEmails = [...emails];

  // Priority filter (based on AI priority score)
  if (filters.priority) {
    filteredEmails = filteredEmails.filter(email => {
      const score = email.ai_priority_score || 0;
      switch (filters.priority) {
        case 'high':
          return score >= 7;
        case 'medium':
          return score >= 4 && score < 7;
        case 'low':
          return score < 4;
        default:
          return true;
      }
    });
  }

  // Category filter
  if (filters.category) {
    filteredEmails = filteredEmails.filter(email => 
      email.categories?.includes(filters.category!)
    );
  }

  // Tag filter  
  if (filters.tag) {
    filteredEmails = filteredEmails.filter(email =>
      email.tags?.includes(filters.tag!)
    );
  }

  // AssignedTo filter
  if (filters.assignedTo) {
    filteredEmails = filteredEmails.filter(email => {
      if (!email.assignedto) return false;
      try {
        const assignedTo = typeof email.assignedto === 'string' 
          ? JSON.parse(email.assignedto) 
          : email.assignedto;
        return assignedTo?.id === filters.assignedTo;
      } catch {
        return false;
      }
    });
  }

  return filteredEmails;
};
